import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Avatar from './Avatar'
import NotificationBell from './NotificationBell'
import MessageBell from './MessageBell'
import CartIcon from './CartIcon'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)

  const toggleMenu = () => setIsOpen(!isOpen)
  const closeMenu = () => setIsOpen(false)

  const handleLogout = () => {
    logout()
    closeMenu()
    setDropdownOpen(false)
    navigate('/')
  }

  // The navbar sizes to its content (it wraps at narrow widths, and gains the device's
  // safe-area inset on notched phones), so publish its real height as --navbar-height.
  // Everything that offsets itself below the bar (body padding, toasts, min-heights,
  // scroll margins) reads that one variable, so none of them can drift from the bar.
  useLayoutEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--navbar-height', `${nav.getBoundingClientRect().height}px`)
    publish()
    const observer = new ResizeObserver(publish)
    // border-box: the safe-area inset is padding, which the default content-box misses.
    observer.observe(nav, { box: 'border-box' })
    return () => {
      observer.disconnect()
      root.style.removeProperty('--navbar-height')
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-container">
        <Link to={user ? '/dashboard' : '/'} className="logo">
          <img src="/IGotThat_logo_bk.svg" alt="I Got That U.S." className="logo-img" width="230" height="34" />
        </Link>

        {user ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
            <CartIcon />
            <MessageBell />
            <NotificationBell />
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-label="User menu"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Avatar
                firstName={user.firstName}
                lastName={user.lastName}
                avatar={user.avatar || user.avatarUrl}
                size={40}
              />
            </button>

            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 0.5rem)',
                right: 0,
                width: '240px',
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                zIndex: 1001,
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {/* User info */}
                <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
                  <Avatar
                    firstName={user.firstName}
                    lastName={user.lastName}
                    avatar={user.avatar || user.avatarUrl}
                    size={64}
                    style={{ margin: '0 auto 0.75rem' }}
                  />
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '1rem' }}>
                    {user.name}
                  </div>
                  {memberSince && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      Member since {memberSince}
                    </div>
                  )}
                </div>

                <div style={{ height: '1px', background: 'var(--border)' }} />

                {/* Menu items */}
                <Link
                  to="/dashboard"
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <span className="mdi mdi-view-dashboard-outline" style={{ fontSize: '1.25rem' }} />
                  Dashboard
                </Link>
                <Link
                  to="/my-rentals"
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <span className="mdi mdi-calendar-check" style={{ fontSize: '1.25rem' }} />
                  My Rentals
                </Link>
                <Link
                  to="/earnings"
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <span className="mdi mdi-cash-multiple" style={{ fontSize: '1.25rem' }} />
                  Earnings
                </Link>
                <Link
                  to="/browse"
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <span className="mdi mdi-magnify" style={{ fontSize: '1.25rem' }} />
                  Browse Items
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <span className="mdi mdi-account-outline" style={{ fontSize: '1.25rem' }} />
                  Profile
                </Link>
                {user.role === 'ADMIN' && (
                  <Link
                    to="/admin"
                    onClick={() => setDropdownOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '8px',
                      background: 'var(--bg-tint)',
                      color: 'var(--forest-green)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}
                  >
                    <span className="mdi mdi-shield-crown-outline" style={{ fontSize: '1.25rem' }} />
                    Switch to Admin
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  <span className="mdi mdi-logout" style={{ fontSize: '1.25rem' }} />
                  Log Out
                </button>
              </div>
            )}
          </div>
          </div>
        ) : (
          <>
            <button className={`hamburger ${isOpen ? 'open' : ''}`} onClick={toggleMenu} aria-label="Menu">
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div className={`nav-links ${isOpen ? 'open' : ''}`} id="navLinks">
              <Link to="/how-it-works" className="nav-link" onClick={closeMenu}>How it Works</Link>
              <Link to="/browse" className="nav-link" onClick={closeMenu}>Browse Items</Link>
              <Link to="/login" className="nav-link" onClick={closeMenu}>Log In</Link>
              <Link
                to="/login"
                className="btn btn-outline"
                onClick={closeMenu}
              >
                List Your Items
              </Link>
              <Link to="/register" className="btn btn-primary" onClick={closeMenu}>Sign Up</Link>
            </div>
          </>
        )}
      </div>
    </nav>
  )
}
